import ModalTemplate, {modalPageDecorator} from "./ModalTemplate";
import ValidatedComponent from "../components/ValidatedComponent";
import Validator from "validatorjs";
import LoadingIndicator from "../components/LoadingIndicator";
import * as userActions from "../actions/user";
import {connect} from "redux-vue";
import PasswordInput from "../components/PasswordInput";

const RegisterModal = modalPageDecorator({
  props: ["app", "user", "login", "register", "uploadPhoto", "setEnrollMaster"],

  data() {
    return {
      formData: {
        firstname: "",
        lastname: "",
        email: "",
        email_confirmation: "",
        country: "",
        role: "",
        role_name: "",
        password: "",
        password_confirmation: ""
      },
      formRules: {
        firstname: "required|max:30",
        lastname: "required|max:30",
        email: "required|email",
        email_confirmation: "required|same:email",
        country: "required",
        role: "required",
        role_name: "",
        password: "required|min:8",
        password_confirmation: "required|same:password"
      },
      formErrors: null,
      fileData: null,
      formLabels: {
        firstname: this.$t('register_modal.first_name_label'),
        lastname: this.$t('register_modal.last_name_label'),
        email: this.$t('register_modal.email_label'),
        email_confirmation: this.$t('register_modal.email_confirmation_label'),
        country: this.$t('register_modal.country_label'),
        role: this.$t('register_modal.role_label'),
        role_name: this.$t('register_modal.role_name_label'),
        password: this.$t('register_modal.password_label'),
        password_confirmation: this.$t('register_modal.password_confirmation_label')
      },
    }
  },

  computed: {
    roleNameVisible() {
      const otherRole = this.app.roles.find(
        i => i.role.toLowerCase() === "other"
      );
      return otherRole && otherRole.id === this.formData.role;
    }
  },

  methods: {
    /**
     *
     */
    onAvatarEditClick () {
      this.$refs.aviInput && this.$refs.aviInput.click();
    },

    /**
     *
     * @param event
     */
    uploadProfileAvatar (event) {
      if (event.target.files.length && !this.user.photoUpload) {
        const file = event.target.files[0];
        this.fileData = new FormData();
        this.fileData.append("photo", file);
        this.$refs.aviFilename.textContent = file.name;
      }
    },

    /**
     *
     * @param event
     * @returns {Promise<void>}
     */
    async onSubmitPress (event) {
      event.preventDefault();

      if (this.roleNameVisible) {
        this.formRules.role_name = "required";
      } else {
        this.formRules.role_name = "";
      }

      const validation = new Validator(this.formData, this.formRules);

      validation.setAttributeFormatter((attribute) =>
        this.formLabels[attribute].toLowerCase()
      );

      if (validation.passes()) {
        this.formErrors = null;

        await this.register(this.formData);

        if (this.user.registerSuccess) {
          await this.login(this.formData.email, this.formData.password);

          if (this.user.loginSuccess) {
            if (this.fileData) {
              this.uploadPhoto(this.fileData);
            }

            if (this.$route.name !== "CoursesPage") {
              const enrollMaster = this.user.enrollMaster ? 1 : 0;
              this.$router.replace({ name: "CoursesPage", query: { enrollMaster } });
            }
          }
        }

        if (this.user.registerError) {
          this.formErrors = this.user.registerError.response.data.errors;
        }
      } else {
        this.formErrors = validation.errors.all();
      }

    },

    /**
     *
     * @param event
     */
    onChangeEnrollMaster(event) {
      this.setEnrollMaster(event.target.checked);
    },
  },

  /**
   *
   * @param h
   * @returns {*}
   */
  render(h) {
    return (
      <ModalTemplate>
        <div class="m-2 lg:my-12">
          <div class="bg-white container lg:px-12 md:max-w-4xl mx-auto rounded shadow-lg relative z-50">
            <div class="p-5 lg:pt-5 pb-8 lg:pb-10">
              <div onClick={this.dismiss} class="cursor-pointer z-50 absolute right-0 top-0 m-4">
                <i class="mdi mdi-close text-3xl lg:text-4xl leading-none"></i>
              </div>

              <h3 class="lg:text-xl font-bold mb-3 lg:mt-6">
                {this.$t('register_modal.h1')}
              </h3>
              <p class="font-bold text-xs mb-3 lg:mb-5">
                {this.$t('register_modal.h2')}
              </p>
              <p class="font-bold text-xs">
                {this.$t('register_modal.registered_user')}{' '}
                <router-link to={{name: 'LoginModal'}} replace class="text-blue-500 hover:underline font-normal">
                  {this.$t('register_modal.sign_in')}
                </router-link>
              </p>

              <form>
                <div class="flex flex-wrap mt-8 lg:-mx-10">
                  <div class="w-full lg:w-6/12 lg:px-10 mb-16 lg:mb-0">
                    <p class="lg:text-lg font-bold mb-4">
                      {this.$t('register_modal.basic_info')}{' '}
                      <span class="text-xs italic font-normal">- {this.$t('required')}</span>
                    </p>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="firstname">
                        {this.formLabels.firstname}
                      </label>
                      <ValidatedComponent
                        name="firstname"
                        errors={this.formErrors}
                        renderComponent={classname => (
                          <input
                            class={"bg-gray-200 text-sm appearance-none border rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline " + classname}
                            id="firstname" type="text" v-model={this.formData.firstname}/>
                        )}
                      />
                    </div>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="surname">
                        {this.formLabels.lastname}
                      </label>
                      <ValidatedComponent
                        name="lastname"
                        errors={this.formErrors}
                        renderComponent={classname => (
                          <input
                            class={"bg-gray-200 text-sm appearance-none border border-faded-200 rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline " + classname}
                            id="lastname" type="text" v-model={this.formData.lastname}/>
                        )}
                      />
                    </div>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="country">
                        {this.formLabels.country}
                      </label>
                      <ValidatedComponent
                        name="country"
                        errors={this.formErrors}
                        renderComponent={classname => (
                          <div class="relative">
                            <select
                              class={"block appearance-none w-full bg-gray-200 text-sm border border-faded-200 text-gray-700 p-2 rounded leading-tight focus:outline-none focus:shadow-outline " + classname}
                              id="country"
                              v-model={this.formData.country}
                            >
                              <option selected disabled value=""></option>
                              {this.app.countries.map(i => (
                                <option value={i.id} key={i.id}>
                                  {i.country}
                                </option>
                              ))}
                            </select>
                            <div
                              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                              <i class="mdi mdi-chevron-down"></i>
                            </div>
                          </div>
                        )}
                      />
                    </div>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="role">
                        {this.formLabels.role}
                      </label>
                      <ValidatedComponent
                        name="role"
                        errors={this.formErrors}
                        renderComponent={classname => (
                          <div class="relative">
                            <select
                              class={"block appearance-none w-full bg-gray-200 text-sm border border-faded-200 text-gray-700 p-2 rounded leading-tight focus:outline-none focus:shadow-outline " + classname}
                              id="role"
                              v-model={this.formData.role}
                            >
                              <option selected disabled value=""></option>
                              {this.app.roles.map(i => (
                                <option value={i.id} key={i.id}>
                                  {i.role}
                                </option>
                              ))}
                            </select>
                            <div
                              class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                              <i class="mdi mdi-chevron-down"></i>
                            </div>
                          </div>
                        )}
                      />
                    </div>

                    {this.roleNameVisible && (
                      <div class="mb-4">
                        <label class="block text-gray-700 text-sm mb-2" for="role_name">
                          {this.formLabels.role_name}
                        </label>
                        <ValidatedComponent
                          name="role_name"
                          errors={this.formErrors}
                          renderComponent={classname => (
                            <input
                              class={"bg-gray-200 text-sm appearance-none border border-faded-200 rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline " + classname}
                              id="role_name" type="text" v-model={this.formData.role_name}/>
                          )}
                        />
                      </div>
                    )}

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="password">
                        {this.formLabels.password}
                      </label>
                      <ValidatedComponent
                        name="password"
                        errors={this.formErrors}
                        renderComponent={classname => (
                          <PasswordInput
                            v-model={this.formData.password}
                            classname={classname}
                          />
                        )}
                      />
                    </div>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="password_confirmation">
                        {this.formLabels.password_confirmation}
                      </label>
                      <ValidatedComponent
                        name="password_confirmation"
                        errors={this.formErrors}
                        renderComponent={classname => (
                          <PasswordInput
                            id="password_confirmation"
                            v-model={this.formData.password_confirmation}
                            classname={classname}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div class="w-full lg:w-6/12 lg:px-10">
                    <p class="lg:text-lg font-bold mb-4">
                      {this.$t('register_modal.contact_info')}{' '}
                      <span class="text-xs italic font-normal">- {this.$t('required')}</span>
                    </p>

                    <div class="mb-4">
                      <label class="block text-gray-700 text-sm mb-2" for="email">
                        {this.formLabels.email}
                      </label>
                      <ValidatedComponent
                        name="email"
                        errors={this.formErrors}
                        renderComponent={classname => (
                          <input
                            class={'bg-gray-200 text-sm appearance-none border border-faded-200 rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ' + classname}
                            id="email" type="email" v-model={this.formData.email}/>
                        )}
                      />
                    </div>

                    <div class="mb-12">
                      <label class="block text-gray-700 text-sm mb-2" for="confirm-email">
                        {this.formLabels.email_confirmation}
                      </label>
                      <ValidatedComponent
                        name="email_confirmation"
                        errors={this.formErrors}
                        renderComponent={classname => (
                          <input
                            class={'bg-gray-200 text-sm appearance-none border border-faded-200 rounded w-full p-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ' + classname}
                            id="email_confirmation" type="email" v-model={this.formData.email_confirmation}/>
                        )}
                      />
                    </div>

                    <p class="lg:text-lg font-bold mb-4 border-b border-faded-200">
                      {this.$t('register_modal.photo')} <span class="text-xs italic font-normal">- {this.$t('register_modal.optional')}</span>
                    </p>

                    <div class="mb-10">
                      <p class="mt-5 lg:mt-0 mb-1 lg:mb-4 text-xs lg:text-sm" ref="aviFilename">XXXX.jpg</p>
                      <button
                        class="px-3 p-1 rounded-full focus:outline-none focus:shadow-outline text-xs lg:text-sm border-blue-500 border"
                        type="button"
                        onClick={this.onAvatarEditClick}
                        disabled={this.user.photoUpload}
                      >
                        {this.user.photoUpload && <LoadingIndicator/>}
                        {this.user.photoUpload || (
                          <fragment>
                            <i class="mdi mdi-upload mr-2"></i>
                            {this.$t('register_modal.upload_new')}...
                          </fragment>
                        )}
                      </button>
                      <input type="file" ref="aviInput" accept="image/png,image/gif,image/jpeg"
                             onChange={this.uploadProfileAvatar} class="hidden"/>
                    </div>

                    <label for="master_enroll" class="flex items-center cursor-pointer my-10">
                      <div class="relative">
                        <input id="master_enroll" type="checkbox" class="hidden" checked={this.user.enrollMaster} onChange={this.onChangeEnrollMaster}/>
                        <div class="w-10 h-4 bg-gray-400 rounded-full shadow-inner"></div>
                        <div class="custom-input-toggle absolute w-6 h-6 bg-white rounded-full shadow inset-y-0 left-0"></div>
                      </div>
                      <div class="ml-3 text-gray-700 text-sm">
                        {this.$t('register_modal.enroll_mlm_label')}
                      </div>
                    </label>

                    <button
                      class="mb-3 lg:mb-0 px-5 p-2 lg:p-3 rounded-full bg-action hover:bg-action-dark focus:outline-none focus:shadow-outline text-base text-white "
                      type="submit" onClick={this.onSubmitPress} disabled={this.user.register}>
                      {this.user.register && <LoadingIndicator/>}
                      {this.user.register || (
                        <fragment>
                          {this.$t('register_modal.register_now')}
                          <i class="mdi mdi-arrow-right ml-5"></i>
                        </fragment>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </ModalTemplate>
    );
  }
});

function mapStateToProps(state) {
  return {
    app: state.app,
    user: state.user
  };
}

function mapActionToProps(dispatch) {
  return {
    login: async (email, password) =>
      await dispatch(userActions.login(email, password)),
    register: async data => await dispatch(userActions.register(data)),
    uploadPhoto: (data) => dispatch(userActions.uploadPhoto(data)),
    setEnrollMaster: data => dispatch(userActions.setEnrollMaster(data))
  };
}

export default connect(mapStateToProps, mapActionToProps)(RegisterModal);